// 상단 고정줄 — 두 층 헤더 밴드
//
// 정본이 계약한 모양이다. 위층은 중앙에 큰 UTC 시계와 오차, 아래층은 타입 배지 ·
// 남은 시간(`T− --:--` 자리 고정) · delta 최소 요약, 그리고 **KO/EN 은 우측 끝 한
// 자리**다. 진입 화면에서는 위층과 KO/EN 만 산다.
//
// **이 스텝이 실제로 세우는 것은 위층과 KO/EN 자리뿐이다.** 아래층은 세 상태(타입 ·
// 남은 시간 · 요약)가 전부 있어야 관측되는데 그 값을 만드는 스텝이 셋으로 갈려 있다.
// 그래서 아래층은 **자리를 고정한 채 값을 받는 모양**으로만 세우고, 값이 없을 때
// 무엇이 뜨는지를 여기서 못박는다 — 자리가 흔들리지 않는 것 자체가 계약이기 때문이다.
// dispatch 전에 절대 시각을 보여 주지 않는다는 규칙이 고정줄에도 그대로 적용돼서,
// 값이 없는 남은 시간은 빈칸이 아니라 `T− --:--` 다.
//
// KO/EN 을 절대 배치로 우측 끝에 고정한 이유는 판정 계약이 **다른 자리에서 발견되면
// 불통**을 내기 때문이다. 층 안에 흘려 두면 아래층이 없는 화면에서 위층 안으로 밀려
// 들어가 자리가 화면마다 달라진다.
//
// **고정줄의 폭은 모든 화면에서 하나다** (2026-10-15 사용자 결정 「같은 자리로 고침」). 「전 화면 이 자리 하나」를
// 화면이 달라도 같은 x 로 읽는다. 전에는 고정줄이 그 화면 본문의 열을 따라가서, 1280 에서 KO 버튼이 진입과 열람은
// x 818, 관리 코드 없는 구성은 882, 구성은 1106 에 섰다(D4 「후」 판 1, 기준 판은 네 화면 모두 1066.8). 폭은 가장 넓은
// 구성 화면의 열(72rem)에 맞췄다(세션 선택, 근거는 그 화면의 고정줄과 본문이 이미 그 열을 쓰고 있어 구성 화면의 자리가
// 그대로 남는다는 것). 대가로 진입과 열람의 고정줄은 본문 열보다 넓다(사용자가 받아들인 대가). 스크롤 막대가 자리를 차지하는
// 데스크톱 브라우저에서도 세로 스크롤 유무로 열이 옮기지 않게, 그런 브라우저에서는 막대 자리를 늘 비워 둔다(`src/web/shell/scrollbar.ts`
// 가 막대 폭을 재어 표시를 달고 `src/web/index.css` 의 루트 규칙이 비운다, 2026-10-15 세션 선택, 사용자 확인 안 함).
//
// 폭 넷(340 폴드, 360 폰, 768 태블릿, 1280 데스크톱)에서 가로 스크롤이 없어야
// 한다. 좁은 쪽에서 무너지는 자리가 둘이라 각각 다르게 막았다. 시계는 글자 크기를
// 줄이고, 요약은 잘라 낸다(truncate). 요약을 줄바꿈으로 흘리지 않는 이유는 밴드
// 높이가 변하면 그 아래 본문이 통째로 튀기 때문이다. 「다음」 요약은 조각마다 줄어드는
// 순서가 다르다(줄 수, 이름 순으로 줄고 간격 숫자는 줄지 않는다, 2026-10-15 사용자 결정).

import { useLayoutEffect, useRef } from 'react'

import type { SkewView } from '../clock/sync.ts'
import type { RemainingState, SummaryParts } from './band.ts'
import { entry as entryStrings, shell, type Lang, LANGS } from '../../i18n/index.ts'
import { Icon, IconTile, Wordmark } from '../ui/icons.tsx'

export type OpType = 'attack' | 'defense'

export interface TopBarOps {
  kind?: 'ops'
  /** 지금 보는 탭. 구성 화면은 CT 가 연 탭(명부와 ATTACK 은 attack), 열람은 서 있는 탭이다. */
  opType: OpType | null
  /** 남은 시간 표시값. null 이면 자리를 고정한 `T− --:--` 가 뜬다. 모든 개시가 지나면 상태 글자가 온다. */
  remaining: string | null
  /**
   * 남은 시간 칸의 상태. 계산(`src/web/shell/band.ts`)이 함께 넘긴다. 칸의 잉크(`data-state`)를 이 값으로 고른다. 표시 글자를 비교해
   * 고르지 않는다(글자는 언어와 문안에 따라 바뀐다).
   */
  remainingState: RemainingState
  /** delta 최소 요약. 빈 문자열이면 「ops 없음」이 뜬다. */
  summary: string
  /**
   * 「다음」 요약의 조각 넷(`src/web/shell/band.ts`). 있으면 조각마다 따로 그려 폭이 모자랄 때 줄 수 조각부터 줄이고, 간격 숫자는 줄이지
   * 않는다. 조각을 이으면 `summary` 와 같다. 없으면 요약을 한 덩어리로 그리고 끝을 자른다.
   */
  summaryParts?: SummaryParts
}

/**
 * room 에 붙지 못한 구성 화면의 아래층(D4 미리보기 c2, 2026-10-13 사용자 지시).
 *
 * 관리 코드가 없으면 room 을 읽지 못하므로 타입도 남은 시간도 요약도 모른다. 그때 「타입 미정 / T− --:-- /
 * ops 없음」을 보이면 모르는 것을 없다고 말한다. 아래층 높이는 그대로 두고(본문이 튀지 않게) 상태 한 줄만 둔다.
 */
export interface TopBarNotice {
  kind: 'notice'
  /**
   * 왜 붙지 못했나. 칩 아이콘을 이 값으로 고른다(관리 코드 필요는 열쇠, 다른 탭은 겹친 층). 글자를 비교해 고르면 껍데기와 고정줄이
   * 다른 언어 묶음을 읽는 날 아이콘이 조용히 틀린다(c2 반증 검사의 경고). 주지 않으면 관리 코드 필요로 본다.
   */
  reason?: 'need-code' | 'other-tab'
  /** 상태 글자. 「관리 코드 필요」 또는 「다른 탭에서 사용 중」. */
  text: string
}

export type TopBarLower = TopBarOps | TopBarNotice

export interface TopBarProps {
  /** 서버 시각으로 보정한 지금. 밀리초 정수다. */
  utcMillis: number
  /**
   * 편차 칸에 그릴 것 한 벌. **밀리초와 진행 표시 둘로 나눠 받던 것을 합쳤다.**
   *
   * 나눠 받으면 「값이 있는데 측정 중」 같은 조합을 타입이 못 막고, 실제로 계측 프로브가
   * 진행 표시를 조건 없이 항상 넘겨서 수렴한 뒤에도 「측정 중 (8/3)」을 달 참이었다.
   * 필수 한 덩이로 만들면 화면 다섯이 전부 컴파일에서 드러나 빠뜨릴 수 없게 된다.
   */
  skew: SkewView
  /** 아래층. null 이면 위층과 KO/EN 만 산다(진입 화면이 그 모양이다). */
  lower?: TopBarLower | null
  lang: Lang
  onLangChange: (lang: Lang) => void
  /**
   * 1280 폭 이상에서 위층 시계를 한 칸 키운다. 구성 화면(관리 코드로 들어간 화면)만 켠다.
   *
   * 예전에는 이 크기를 「그 화면 본문이 쓰는 열」(넓은 열이면 키움)에서 읽었다. 고정줄 폭이 모든 화면에서 하나가 된 뒤로(2026-10-15
   * 사용자 결정) 열은 더 이상 화면마다 갈리지 않으므로 크기만 따로 받는다. 화면마다의 시계 크기는 그 결정 전과 같다.
   */
  largeClock?: boolean
  /**
   * 위층 왼쪽 끝에 제품 표지를 둔다(2026-10-13 사용자가 c2 를 보고 고름). 구성 화면만 켜고, 1024 폭 이상에서만 보인다. 표지는 고정줄 열의
   * 왼쪽 끝에 서고, `bodyCard` 를 켠 상태에서는 본문 카드의 왼쪽 끝에 선다. 열람 화면은 켜지 않는다(위층이 이미 시계와 KO/EN 으로 찼고,
   * 헌법상 숫자 곁 장식을 늘리지 않는다).
   */
  brand?: boolean
  /**
   * 본문이 가운데 카드 한 장인 상태(관리 코드 필요, 다른 탭에서 사용 중)다. 켜면 표지와 아래층 상태 칩을 그 카드의 왼쪽 끝에 맞춘다.
   *
   * 이 정렬은 2026-10-13 세션 선택(비평 둘이 단 조건, 「본문이 가운데 좁은 열이면 표지를 그 열 왼쪽에」)이다. 그때는 고정줄 열을 카드 쪽
   * 폭으로 좁혀 맞췄는데, 2026-10-15 사용자 결정으로 고정줄 폭이 모든 화면에서 하나가 됐으므로 열은 그대로 두고 두 자리만 옮긴다(세션
   * 선택). 열 클래스를 바꾸지 않으므로 KO/EN 자리는 다른 화면과 같다. 자리 계산은 `src/web/index.css` 의 `header[data-body='card']` 규칙이다.
   */
  bodyCard?: boolean
  /**
   * 고정줄 아래 가장자리의 장식선을 걷는다. 구성 화면은 고정줄 바로 아래에 어두운 머리 띠가 붙어 둘이 한 덩어리의 머리가 되므로, 그 사이의
   * 선이 머리를 둘로 끊는다(비평 둘의 지적, 2026-10-13).
   */
  joined?: boolean
}

/**
 * 인자를 준 Date 는 값 변환이지 현재 시각 읽기가 아니다 — lint 규칙이 막는 것은 인자
 * 없는 쪽이다. 지금이 몇 시인지는 이 컴포넌트가 묻지 않고 위에서 받는다.
 */
function formatUtc(millis: number): string {
  return new Date(millis).toISOString().replace('T', ' ').replace('Z', ' UTC')
}

/**
 * 편차를 화면 글자로 적는다. **화면의 + 는 기기 시계가 서버보다 앞섰다는 뜻이다.**
 *
 * 들고 있는 값은 서버 빼기 기기(`src/web/clock/sync.ts` 의 `offsetMs`)라 기기가 앞서면 음수다.
 * 그대로 적었더니 기기가 1.5초 빠른데 「−1500 ms」가 떠서 대리 관측 둘이 읽지 못했다. 라벨이
 * 「기기 시계」이므로 그 뒤의 부호는 기기의 앞섬과 뒤짐을 말해야 한다(2026-10-02 사용자 결정,
 * T4 개정 둘째). 값은 그대로 두고 여기서만 뒤집는다. 값을 뒤집으면 보정 계산이 따라 뒤집힌다.
 */
function formatSkew(millis: number): string {
  const device = -millis
  // 부호를 유니코드 빼기표로 적는다. 하이픈은 좁은 폭에서 앞 글자에 붙어 읽히지 않는다.
  return `${device >= 0 ? '+' : '−'}${String(Math.abs(device))} ms`
}

/**
 * 이 줄의 높이를 문서 전체의 CSS 변수 `--topbar-h` 로 알린다.
 *
 * attack 신호 존의 머리(이름과 큰 카운트다운)가 이 줄 바로 아래에 붙어 있으려면 이 줄의 높이를 알아야 한다. 높이는 두
 * 층 여부와 좁은 폭의 줄바꿈에 따라 바뀌므로 고정값으로 적을 수 없다. 감춰진 화면의 이 줄은 높이가 0 이라 알리지 않는다
 * (열람 화면에는 이 줄이 두 벌 있고 보이는 것은 하나다). 2026-10-05 T6 개정 셋째 대리 관측이 스크롤하면 이름만 이 줄 밑에
 * 숨고 숫자는 남는 것을 잡았다(혼동 방지 규칙 C1).
 */
function useTopbarHeight(): React.RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    const node = ref.current
    if (node === null) return
    const publish = (): void => {
      const height = node.offsetHeight
      if (height > 0) document.documentElement.style.setProperty('--topbar-h', `${String(height)}px`)
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [])
  return ref
}

/**
 * 「다음」 요약 한 줄. 폭이 모자랄 때 줄어드는 순서를 조각마다 다르게 준다(2026-10-15 사용자 결정 「수와 이름을 지킴」).
 *
 * 1. 줄 수 조각이 먼저 말줄임으로 준다. 끝까지 줄어도 말줄임표 한 글자 자리(0.9em)는 남겨 앞에 무엇이 줄었다는 것을 보인다.
 *    0.9em 은 Chromium 이 실제로 그리는 말줄임표가 들어가는 폭이다. 이 글꼴은 공백이 든 첫 묶음에 「…」 가 없어 Chromium 이 마침표
 *    셋 「...」 를 그린다(실측 11px 에서 9px, 14px 에서 12px. 「…」 한 글자는 8px 와 11px, 2026-10-15 세션 계측).
 * 2. 그래도 모자라면 이름이 말줄임으로 준다.
 * 3. 이름 앞 낱말과 간격 숫자는 줄지 않는다.
 *
 * 순서를 줄임 비율의 크기 차로 주지 않는다. 비율로는 줄 수 조각이 남아 있는 동안에도 이름이 소수 픽셀씩 줄어, 그만큼
 * 이름 끝에 말줄임표가 선다. 대신 뒤 묶음(낱말, 이름, 숫자)은 줄지 않되 최대 폭을 「줄 전체 빼기 0.9em」으로 막고, 그 안에서 이름만 준다.
 * 줄 수 조각은 남은 폭을 받는다. 그래서 줄 수 조각이 다 줄기 전에는 이름이 한 픽셀도 줄지 않는다.
 *
 * 줄 수 조각 맨 앞의 빈 상자 하나는 다 줄었을 때 말줄임표만 서게 하려는 것이다. 말줄임 규칙은 줄의 첫 글자를 말줄임표로 바꾸지 않고
 * 잘라 그린다(CSS 명세의 text-overflow 절, 첫 글자 또는 첫 원자 인라인 요소). 그래서 이 상자가 없으면 「r」가 남고 말줄임표가 반쯤
 * 잘려 「r..」로 섰다(2026-10-15 세션이 3배 사진으로 확인). 폭 0 인 빈 상자가 첫 원자 인라인 요소가 되면 글자는 모두 말줄임표로 바뀔 수
 * 있어, 사용자가 본 미리보기(「… 다음 DragonSl… +0:00.5」)처럼 선다. 글자가 없어 textContent 는 그대로다. 원자 인라인이면 무엇이든
 * 되므로 이미 쓰는 표시 값(인라인 플렉스)을 style 로 준다. 새 표시 클래스 이름을 소스에 들이면 이 테마에서는 간격 토큰과 겹치는 규칙이
 * 생길 수 있다.
 *
 * 글자는 조각을 이은 것이라 요약 상자의 textContent 는 한 덩어리였을 때와 같다(판정 계약과 브라우저 검사가 그 글자를 읽는다).
 * 조각 사이의 빈칸은 조각 안에 있으므로 빈칸이 있는 조각은 빈칸을 그대로 그린다(줄 끝 빈칸이 접히지 않게).
 *
 * 받아들인 차이 하나(2026-10-15 세션 판단, 반증 검토의 지적): 조각이 플렉스 항목(블록 수준)이라 innerText 는 조각 사이에서 줄이 바뀌고
 * (「rally 3 줄,」 다음 줄 「 다음 」 …), 접근성 트리의 글자 마디도 조각 넷으로 갈린다. 그래서 화면 읽개의 탐색 모드가 조각마다 멈춰
 * 읽을 수 있고, 복사하면 줄바꿈이 섞인다. textContent 를 바꾸지 않고 이것을 막는 방법(상자에 aria-label 을 다는 것 등)은 낭독기마다
 * 결과가 달라 실기기 없이 고르지 않았다(확인 안 함). 관측 스크립트가 이 글자를 읽을 때는 innerText 가 아니라 textContent 를 쓴다.
 */
/** 줄 수 조각 맨 앞의 폭 0 원자 인라인 상자. 아래 함수 설명의 마지막 문단이 이유다. */
const LEAD_ATOM: React.CSSProperties = { display: 'inline-flex', width: 0 }

function SummaryLine(props: { parts: SummaryParts }): React.ReactElement {
  const { parts } = props
  return (
    <span data-testid="topbar-summary" className="flex min-w-0 flex-1 overflow-hidden text-xs text-ink-dim sm:text-sm">
      <span data-testid="topbar-summary-count" className="min-w-0 truncate">
        <span aria-hidden="true" style={LEAD_ATOM} />
        {parts.count}
      </span>
      <span className={`flex min-w-0 shrink-0 ${parts.count === '' ? 'max-w-full' : 'max-w-[calc(100%-0.9em)]'}`}>
        <span data-testid="topbar-summary-lead" className="shrink-0 whitespace-pre">
          {parts.lead}
        </span>
        <span data-testid="topbar-summary-roster" className="min-w-0 truncate">
          {parts.roster}
        </span>
        <span data-testid="topbar-summary-offset" className="shrink-0 whitespace-pre">
          {parts.tail}
        </span>
      </span>
    </span>
  )
}

export function TopBar(props: TopBarProps): React.ReactElement {
  const t = shell(props.lang)
  const lower = props.lower ?? null
  const ref = useTopbarHeight()

  return (
    <header
      ref={ref}
      data-testid="topbar"
      // 본문이 카드 한 장인 상태의 표시. 표지와 아래층 상태 칩의 자리를 index.css 가 이 값으로 고른다(열 클래스는 그대로).
      data-body={props.bodyCard === true ? 'card' : undefined}
      className="sticky top-0 z-10 bg-surface-sunk/95 shadow-band backdrop-blur"
    >
      {/* 모든 화면이 같은 열이다. 화면마다 열이 갈리면 KO/EN(이 열의 우측 끝)도 화면마다 갈린다. */}
      <div className="relative col-wide">
        {/* 제품 표지(장식 타일 + 제품 이름 글자). 고정줄 열의 왼쪽 끝(본문이 카드 한 장이면 그 카드의 왼쪽 끝)에 선다. 진입 카드의
            워드마크와 같은 글자(「ops」만 오렌지)다. */}
        {props.brand === true && (
          <div data-testid="topbar-brand" className="brand hidden lg:flex">
            <IconTile name="mark" tone="accent" size="md" />
            <span>
              <Wordmark text={entryStrings(props.lang).title} />
            </span>
          </div>
        )}
        <div className="flex flex-col items-center py-line pr-24 sm:px-24">
          <div className="caps flex items-center gap-1 text-xs font-medium text-ink-mute">
            <Icon name="clock" size={12} />
            {t.utcClock}
          </div>
          <div
            data-testid="utc-clock"
            // **벽시계는 화면에서 가장 큰 글자가 아니다** (2026-08-18 타입 스케일 축).
            // 실측에서 데스크톱 여덟 화면 중 여섯에서 이것이 최대였고, 본문 제목의
            // 1.9배였다. 정본이 「장식이 숫자 인지를 이기면 실패다」를 못박았는데 CT 가
            // 읽어야 할 것은 벽시계가 아니라 「지금 무엇을 하는가」다. 한 칸 내린다.
            // **보정을 잃은 동안은 이 줄도 함께 물러선다** (2026-09-01 사용자 결정).
            // 시각은 그대로 흐르고(2026-08-17 결정) 밀리초 자리도 그대로 둔다. 바뀌는 것은
            // 잉크 한 칸뿐이다. 그러지 않으면 아래 줄이 「지금 얼마나 틀어졌는지 모르겠다」고
            // 말하는 그 순간에 이 줄이 밀리초까지 확신에 차서 단언하게 되는데, 그때 이 값은
            // 시계가 뛴 만큼 틀려 있다. 실측에서 이 머신의 계단이 1.9초였고 표시 오차 예산
            // 250밀리초의 여덟 배다. 빈칸보다 나쁜 것이 확신에 찬 오답이라는 것이 이 파일의
            // 다른 자리에서도 이미 선 전제다.
            className={`whitespace-nowrap font-mono text-sm font-semibold leading-tight tracking-tight tabular-nums md:text-lg ${
              props.largeClock === true ? 'xl:text-xl' : ''
            } ${
              props.skew.state === 'live' ? 'text-ink' : 'text-ink-dim'
            }`}
          >
            {formatUtc(props.utcMillis)}
          </div>
          <div
            data-testid="clock-offset"
            className="flex flex-wrap items-baseline justify-center gap-x-1.5"
          >
            {/* **라벨이 보인다.** aria-label 에만 있던 동안 사람은 이 숫자가 무엇인지 몰랐다(2026-10-02
                대리 관측 둘). 값이 아직 없을 때도 서서 「측정 중」이 무엇을 재는지 말한다. */}
            <span
              data-testid="clock-offset-label"
              className="shrink-0 whitespace-nowrap text-xs text-ink-mute"
            >
              {t.clockSkew}
            </span>
            {props.skew.millis !== null && (
              <span
                data-testid="clock-offset-value"
                // 살아 있을 때와 들고만 있을 때를 **잉크 한 칸**으로 가른다. 크기로
                // 가르지 않는 이유는 사다리의 바닥이 이미 `text-xs` 라 좁은 폭에서 더
                // 내려갈 칸이 없어서다. 밝기는 어느 폭에서나 한 칸 남아 있다.
                className={`font-mono text-sm font-medium tabular-nums ${
                  props.skew.state === 'live' ? 'text-ink-dim' : 'text-ink-mute'
                }`}
              >
                {formatSkew(props.skew.millis)}
              </span>
            )}
            {props.skew.state !== 'live' && (
              <span
                data-testid="clock-offset-note"
                // `whitespace-nowrap` 이 없으면 좁은 폭에서 「연결 없음」이 음절마다
                // 접혀 세로 막대가 되고, 브라우저 검사의 짓눌림 판정이 그것을 잡는다.
                className="shrink-0 whitespace-nowrap text-xs text-ink-off"
              >
                {/* 들고 있는 값이 없는 끊김은 카운트다운이 기기 시계로 흐르는 끊김이다(시계 점프로
                    값을 버렸거나 한 번도 못 붙음). 값을 든 끊김과 낱말을 가른다(2026-10-12 사용자). */}
                {props.skew.state === 'offline'
                  ? props.skew.millis === null
                    ? t.clockSkewOfflineDeviceClock
                    : t.clockSkewOffline
                  : `${t.clockSkewMeasuring} (${props.skew.progress})`}
              </span>
            )}
          </div>
        </div>

        {lower !== null && lower.kind === 'notice' && (
          <div
            data-testid="topbar-lower"
            data-state="notice"
            className="flex min-h-10 items-center gap-2 border-t border-line-hair py-1.5 text-sm"
          >
            {/* 중립 칩에 주 잉크 14px. 관리 코드가 없으면 열쇠, 다른 탭이 이어 쓰면 겹친 층(탭) 아이콘(`reason` 으로 고른다). 다시 넣는
                동작은 본문의 주 버튼 하나라 이 칩은 누를 것이 아니다. */}
            <span data-testid="topbar-notice" className="chip chip-notice shrink-0">
              <Icon name={lower.reason === 'other-tab' ? 'layers' : 'key'} size={14} />
              {lower.text}
            </span>
          </div>
        )}

        {lower !== null && lower.kind !== 'notice' && (
          <div
            data-testid="topbar-lower"
            // 640 미만에서 요소 사이 간격은 4px 다(2026-10-15 세션 선택, 그 전 6px). 340 에서 줄 수 조각이 숫자와 말줄임표까지
            // (「rally 3...」, 11px 에서 41px) 담으려면 두 간격에서 4px 를 돌려받아야 했다(그 전 조각 40.4px, 「rally ...」로 섰다).
            // 칩과 남은 시간 칸의 안쪽 여백은 그대로다. 경위와 전후 계측은 D4 진단 계획서의 「판 1 뒤 결정」 절.
            className="flex min-h-10 items-center gap-1 border-t border-line-hair py-1.5 text-sm sm:gap-2"
          >
            <span
              data-testid="topbar-optype"
              // c2: 640 미만에서 칩 안쪽 여백을 줄여 요약 글자에 폭을 돌려준다(360 에서 요약이 잘리던 자리).
              className={`chip caps shrink-0 max-sm:px-1.5 ${
                lower.opType === 'attack' ? 'chip-accent' : lower.opType === 'defense' ? 'chip-cyan' : ''
              }`}
            >
              {/* 640 미만에서는 아이콘을 감춘다(장식). 그만큼 요약 글자가 360 에서 잘리지 않고 다 선다(실측: 요약 151px). */}
              {lower.opType !== null && (
                <Icon name={lower.opType === 'attack' ? 'attack' : 'defense'} size={12} className="max-sm:hidden" />
              )}
              {lower.opType === null
                ? t.typeUnset
                : lower.opType === 'attack'
                  ? t.typeAttack
                  : t.typeDefense}
            </span>
            <span
              data-testid="topbar-remaining"
              // 칸 너비를 고정한다. 숫자(T−0:43.4, T−12:43.4), 자리표시자, 「개시 끝」 어느 것이 와도 요약의 시작점이
              // 움직이지 않게 한다(T− 자리 고정 계약). 10ch 는 가장 긴 글자(영어 상태 글자)에 맞춘 값이다.
              // c2: 640 미만에서는 9ch 로 좁힌다. 가장 긴 숫자 「T−12:43.4」와 상태 글자 둘(「개시 끝」, 「All opened」)이 그 안에 든다(실측은
              // design.md). 넓은 폭은 앞 회차의 10ch 그대로다.
              className="time-chip shrink-0 justify-center font-mono min-w-[calc(9ch+0.75rem)] max-sm:px-1.5 sm:min-w-[calc(10ch+1rem)]"
              data-state={lower.remainingState}
              aria-label={t.remainingLabel}
            >
              {lower.remaining ?? t.remainingIdle}
            </span>
            {lower.summaryParts === undefined ? (
              <span data-testid="topbar-summary" className="min-w-0 flex-1 truncate text-xs text-ink-dim sm:text-sm">
                {lower.summary === '' ? t.summaryEmpty : lower.summary}
              </span>
            ) : (
              <SummaryLine parts={lower.summaryParts} />
            )}
          </div>
        )}

        {/* 우측 끝 한 자리. 층 밖에 두어야 아래층 유무와 무관하게 같은 자리에 선다. */}
        <div className="seg-group absolute right-4 top-1" data-testid="lang-switch">
          {LANGS.map((lang) => (
            <button
              key={lang}
              type="button"
              aria-label={`${t.langSwitch}: ${lang.toUpperCase()}`}
              aria-pressed={lang === props.lang}
              data-testid={`lang-${lang}`}
              className="seg min-w-11 px-2 uppercase"
              onClick={() => {
                props.onLangChange(lang)
              }}
            >
              {lang}
            </button>
          ))}
        </div>
      </div>
      {props.joined !== true && <span aria-hidden="true" className="band-rule" />}
    </header>
  )
}
